import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import ListingBanner from '../../subrouters/ListingInformation/components/ManageListingVisibility/components/ListingBanner';
import LockclosedIcon from 'icons/Lockclosed.icon';
import { useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';
import EyeIcon from 'icons/Eye.icon';
import {
  checkIsArchived,
  checkIsDraft,
  checkIsEnded,
  checkIsNoListing,
  checkIsPrivateDrafted,
  checkIsSold,
  getCreateListingRoute,
} from 'utils';
import { useNavigate } from 'react-router-dom';
import ShieldcheckIcon from 'icons/Shieldcheck.icon';
import ArrowrightIcon from 'icons/Arrowright.icon';
import ManageListingVisibility from '../../subrouters/ListingInformation/components/ManageListingVisibility';
import ListingContext from '../../providers/Listing/Listing.context';

import './VisibilityBanners.styles.scss';
import './VisibilityBanners.styles.responsive.scss';

type VisibilityBannersProps = {
  className?: string;
};

const VisibilityBanners: React.FC<VisibilityBannersProps> = (props) => {
  const { className } = props;

  const classes = classNames('visibility-banners', className);

  const { canManageDeal, dealRoom, setListing, setDealRoom } =
    useContext(ListingContext);

  const { _id, stage, listingStage, listing, wantToBePrivate } = dealRoom || {};

  const { t } = useTranslation();

  const navigate = useNavigate();

  const isArchived = checkIsArchived(stage);
  const isSold = checkIsSold(stage);

  const RenderSellerBanner = useMemo(() => {
    if (checkIsPrivateDrafted(listingStage, wantToBePrivate)) {
      return (
        <ListingBanner
          icon={LockclosedIcon}
          title={t('yourListingIsActiveButPrivate')}
          description={t('yourListingIsActiveButPrivateDescription')}
        >
          <Button
            variant="outline"
            icon={EyeIcon}
            disabled={!canManageDeal}
            onClick={() => {
              if (!canManageDeal) return;

              navigate(getCreateListingRoute(_id, listing._id, listing.type));
            }}
          >
            {t('makePublic')}
          </Button>
        </ListingBanner>
      );
    }

    return (
      <ManageListingVisibility
        dealRoom={dealRoom}
        canManageVisibility={canManageDeal}
        setDealRoom={setDealRoom}
        setListing={setListing}
      />
    );
  }, [
    _id,
    wantToBePrivate,
    listingStage,
    dealRoom,
    canManageDeal,
    listing,
    setDealRoom,
    setListing,
    t,
    navigate,
  ]);

  if (isArchived || isSold) {
    return (
      <div className={classes}>
        <ListingBanner
          title={t(isArchived ? 'dealIsArchived' : 'dealIsSold')}
          icon={LockclosedIcon}
        />
      </div>
    );
  }

  if (checkIsEnded(listingStage)) {
    return (
      <div className={classes}>
        <ListingBanner title={t('campaignExpired')} icon={LockclosedIcon} />
      </div>
    );
  }

  if (checkIsDraft(listingStage) && !wantToBePrivate) {
    return (
      <div className={classes}>
        <ListingBanner
          title={t('draftListingIsIncomplete')}
          description={t('draftListingIncompleteDescription')}
          icon={ShieldcheckIcon}
        >
          <Button
            variant="outline"
            iconPosition="right"
            icon={ArrowrightIcon}
            disabled={!canManageDeal}
            onClick={() =>
              navigate(getCreateListingRoute(_id, listing?._id, listing?.type))
            }
          >
            {t('completeListing')}
          </Button>
        </ListingBanner>
      </div>
    );
  }

  if (checkIsNoListing(listingStage)) return null;

  return <div className={classes}>{RenderSellerBanner}</div>;
};

export default VisibilityBanners;
